import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Trash2, ChevronDown } from "lucide-react";
import api, { fmtPrice, fmtNum, formatApiError, raceTime, userTime, RECENT } from "@/lib/api";
import { RaceTypeBadge } from "@/components/race/RaceTypeBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { QualifyBadge } from "@/components/Eligibility";
import { Chip, RaceDetailBody, trackWas } from "@/pages/Program";

const byTime = (a, b) => (a.start_aest_iso ? Date.parse(a.start_aest_iso) : Infinity) - (b.start_aest_iso ? Date.parse(b.start_aest_iso) : Infinity) || (a.race_number || 0) - (b.race_number || 0);

const Kpi = ({ label, value, accent, testId }) => (
  <div className="panel p-5 fade-up" data-testid={testId}>
    <div className="eyebrow">{label}</div>
    <div className={`mt-2 text-4xl font-display font-bold ${accent || "text-white"}`}>{value}</div>
  </div>
);

export default function Dashboard() {
  const [races, setRaces] = useState(null);
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("preferred");
  const [opened, setOpened] = useState([]);
  const toggleOpen = (id) => setOpened(opened.includes(id) ? opened.filter((x) => x !== id) : [...opened, id]);
  const updated = (u) => setRaces((rs) => rs.map((r) => (r.id === u.id ? u : r)));

  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Brisbane" });
  const load = () => api.get("/races", { params: RECENT() }).then((r) => setRaces(r.data.filter((x) => x.date === today))).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const qualified = useMemo(() => (races || [])
    .filter((r) => (scope === "preferred" ? r.evaluation.preferred : r.evaluation.ratings?.length || r.evaluation.top_pick))
    .sort(byTime), [races, scope]);
  const shown = qualified.filter((r) => `${r.venue} ${r.race_name} ${r.race_class}`.toLowerCase().includes(q.toLowerCase()));

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this race?")) return;
    await api.delete(`/races/${id}`);
    toast.success("Race deleted");
    load();
  };

  const venues = new Set(qualified.map((r) => r.venue).filter(Boolean));
  const states = new Set(qualified.map((r) => r.state).filter(Boolean));
  const now = new Date().toISOString();
  const next = qualified.find((r) => r.start_aest_iso && new Date(r.start_aest_iso).toISOString() > now);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-2" data-testid="dashboard-today">Selection Board • Today {new Date().toLocaleDateString("en-AU", { timeZone: "Australia/Brisbane", weekday: "short", day: "2-digit", month: "short" })}</div>
          <h1 className="text-4xl sm:text-5xl font-bold" data-testid="dashboard-title">Race Dashboard</h1>
          <div className="flex flex-wrap items-center gap-2 mt-4" data-testid="dashboard-scope">
            <Chip on={scope === "preferred"} onClick={() => setScope("preferred")} testId="dashboard-scope-preferred">Preferred Races</Chip>
            <Chip on={scope === "all"} onClick={() => setScope("all")} testId="dashboard-scope-all">All Races</Chip>
          </div>
        </div>
        <Button asChild data-testid="dashboard-import-button" className="font-semibold">
          <Link to="/program"><Plus className="h-4 w-4 mr-1" /> Scan Races</Link>
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label={scope === "preferred" ? "Preferred Races" : "All Races"} value={races ? qualified.length : "–"} accent="text-emerald-400" testId="kpi-qualified" />
        <Kpi label="Venues" value={venues.size} testId="kpi-venues" />
        <Kpi label="States" value={states.size} testId="kpi-states" />
        <Kpi label="Next Jump (your time)" value={next ? userTime(next) : "—"} accent="text-amber-400" testId="kpi-next-jump" />
      </div>

      <div className="panel">
        <div className="flex flex-wrap items-center gap-3 p-4 border-b border-slate-800">
          <div className="eyebrow" data-testid="dashboard-list-title">{scope === "preferred" ? "Preferred Races" : "All Races"} • time order</div>
          <div className="relative ml-auto w-full sm:w-64">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input data-testid="dashboard-search-input" placeholder="Search venue, race, class" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-[#0F172A] h-9" />
          </div>
        </div>
        {races === null ? (
          <div className="p-10 grid place-items-center"><div className="loader-bar" /></div>
        ) : shown.length === 0 ? (
          <div className="p-12 text-center" data-testid="dashboard-empty">
            <p className="text-slate-400">{scope === "preferred" ? "No Preferred Races yet." : "No rated races yet."} Run the scan to find races that meet your rules.</p>
            <Button asChild variant="outline" className="mt-4"><Link to="/program">Go to Race Program</Link></Button>
          </div>
        ) : (
          <div className="scroll-x">
            <table className="w-full text-sm" data-testid="races-table">
              <thead>
                <tr className="text-left eyebrow border-b border-slate-800">
                  <th className="px-4 py-3 font-normal whitespace-nowrap">Time (your time)</th>
                  <th className="px-4 py-3 font-normal">Race</th>
                  <th className="px-4 py-3 font-normal">Dist</th>
                  <th className="px-4 py-3 font-normal">Class</th>
                  <th className="px-4 py-3 font-normal">Track</th>
                  <th className="px-4 py-3 font-normal">Status</th>
                  <th className="px-4 py-3 font-normal">Top Rated</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {shown.map((r, i) => (
                  <Fragment key={r.id}>
                  <tr
                    data-testid={`race-row-${i}`}
                    onClick={() => toggleOpen(r.id)}
                    className={`row-hover border-b border-slate-800/60 cursor-pointer ${opened.includes(r.id) ? "bg-[#0F172A]" : ""}`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap" data-testid={`race-time-${i}`}>
                      <div className="num text-white font-bold whitespace-nowrap">{userTime(r)}</div>
                      {userTime(r) !== raceTime(r) && <div className="num text-[11px] text-slate-500" data-testid={`race-track-time-${i}`}>Track {raceTime(r)}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-white">{r.venue || "Unknown venue"} <span className="num text-emerald-400">R{r.race_number ?? "?"}</span></div>
                      <div className="text-xs text-slate-500 truncate max-w-[300px]">{[r.state, r.race_name].filter(Boolean).join(" • ")}</div>
                    </td>
                    <td className="px-4 py-3 num">{r.distance ? `${fmtNum(r.distance, 0)}m` : "—"}</td>
                    <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{r.race_class || "—"}
                      {r.race_type && <div className="mt-1"><RaceTypeBadge type={r.race_type} testId={`race-type-${i}`} /></div>}</td>
                    <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{r.track_condition || "—"}
                      {trackWas(r) && <div className="num text-[11px] text-amber-300" data-testid={`race-track-was-${i}`}>was {trackWas(r)}</div>}</td>
                    <td className="px-4 py-3">
                      {r.evaluation.preferred && <QualifyBadge qualifies testId={`race-card-qualify-badge-${i}`} />}
                    </td>
                    <td className="px-4 py-3">
                      {r.evaluation.top_pick ? (
                        <span className="flex items-center gap-2">
                          <span className="text-white">{r.evaluation.top_pick.name}</span>
                          <span className="num text-amber-400">{fmtPrice(r.evaluation.top_pick.price)}</span>
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button data-testid={`race-delete-${i}`} onClick={(e) => remove(r.id, e)} className="p-1.5 rounded text-slate-500 hover:text-rose-400 hover:bg-slate-800 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <ChevronDown className={`h-4 w-4 inline text-slate-500 ml-1 transition-transform ${opened.includes(r.id) ? "rotate-180" : ""}`} data-testid={`race-expand-${i}`} />
                    </td>
                  </tr>
                  {opened.includes(r.id) && (
                    <tr className="border-b border-slate-800/60" data-testid={`race-row-detail-${i}`}>
                      <td colSpan={8} className="pt-4 bg-[#0B111D]"><div className="sticky left-0 max-w-[calc(100vw-2.5rem)] sm:max-w-none"><RaceDetailBody race={r} i={i} onUpdated={updated} prefix="dashboard" /></div></td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
